'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { askLive, type AskOutcome, type ModelRef, type QueryResult, type ResultValue, type Visualization } from '../lib/api';
import {
  autoSpan, isGridSpan, resolveTileViz, snapSpan,
  type GridSpan, type WidgetKind,
} from '../lib/widget';
import { downloadDashboardReport, type DashboardItem } from '../lib/report';
import { updateTemplate, type DraftReport } from '../lib/templates';
import { PageLoader } from './PageLoader';
import { Chart, StatTile } from './Chart';
import type { ReportTemplate, TemplateReport } from '../lib/templates';

/** The fixed width of a metric tile — metrics are drag-only, never resized. */
const METRIC_SPAN: GridSpan = 3;

interface Loaded {
  report: TemplateReport;
  outcome: AskOutcome;
  kind: WidgetKind | null;
  /** The visualization the tile renders as — the report's saved choice when valid, else auto. */
  viz?: Visualization;
}

/** A tile ready to place: its content plus the width it occupies on the 12-col grid. */
interface Tile extends Loaded {
  id: string;
  span: GridSpan;
}

/** The two drag zones. A tile can only be reordered within its own zone. */
type Zone = 'metric' | 'content';
const zoneOf = (kind: WidgetKind | null): Zone => (kind === 'kpi' ? 'metric' : 'content');

const SPAN_LABEL: Record<GridSpan, string> = { 3: '¼ width', 4: '⅓ width', 6: '½ width', 12: 'full width' };

/**
 * Renders a report template as a dashboard in two zones. A Metrics zone lays KPI
 * tiles in a compact quarter-width row — drag-only, reorderable among themselves;
 * and a Charts & graphs zone on a 12-column grid where each tile is auto-sized
 * from its widget/data volume and can be dragged AND resized (snap ¼/⅓/½/full). A
 * tile only moves within its own zone — a metric can't be dropped among the charts.
 *
 * "Edit layout" toggles editing; Save persists the order and widths back to the
 * template, which is per-tenant, so a tenant's layout is theirs alone.
 */
export function DashboardView({ template, modelRef, onBack }: { template: ReportTemplate; modelRef: ModelRef; onBack: () => void }): React.JSX.Element {
  const [loaded, setLoaded] = useState<Loaded[] | null>(null);
  // How many of this run's reports have resolved — drives the load percentage.
  const [done, setDone] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const halfPicked = Boolean(from) !== Boolean(to);
  const suffix = halfPicked ? null : rangeSuffix(from, to);

  useEffect(() => {
    if (suffix === null) return; // waiting for the other end of the range
    let cancelled = false;
    setLoaded(null);
    setDone(0);
    void (async () => {
      const results = await Promise.all(
        template.reports.map(async (report) => {
          const outcome = await askLive(report.question + suffix, modelRef, { insights: false });
          if (!cancelled) setDone((d) => d + 1);
          const resolved = outcome.ok && outcome.result ? resolveTileViz(report, outcome.result) : null;
          return { report, outcome, kind: resolved?.kind ?? null, viz: resolved?.viz };
        }),
      );
      if (!cancelled) setLoaded(results);
    })();
    return () => {
      cancelled = true;
    };
  }, [template, modelRef, suffix]);

  // A layout the user just saved this session, so the grid reflects it immediately.
  const [localLayout, setLocalLayout] = useState<{ id: string; span: GridSpan }[] | null>(null);
  useEffect(() => setLocalLayout(null), [template.id]);

  const tiles: Tile[] = useMemo(() => {
    if (!loaded) return [];
    const byId = new Map(loaded.map((l) => [l.report.id, l]));
    const spanFor = (l: Loaded): GridSpan => {
      if (zoneOf(l.kind) === 'metric') return METRIC_SPAN;
      if (isGridSpan(l.report.span)) return l.report.span;
      if (!l.kind) return 6;
      const result = l.outcome.ok ? l.outcome.result : null;
      const cats = result ? result.rows.length : 0;
      return autoSpan(l.kind, cats, cats);
    };
    if (localLayout) {
      return localLayout.flatMap(({ id, span }) => {
        const l = byId.get(id);
        if (!l) return [];
        return [{ ...l, id, span: zoneOf(l.kind) === 'metric' ? METRIC_SPAN : span }];
      });
    }
    return loaded.map((l) => ({ ...l, id: l.report.id, span: spanFor(l) }));
  }, [loaded, localLayout]);

  const metricTiles = useMemo(() => tiles.filter((t) => zoneOf(t.kind) === 'metric'), [tiles]);
  const contentTiles = useMemo(() => tiles.filter((t) => zoneOf(t.kind) === 'content'), [tiles]);
  const tileById = useMemo(() => new Map(tiles.map((t) => [t.id, t])), [tiles]);
  const zoneOfId = (id: string): Zone => zoneOf(tileById.get(id)?.kind ?? null);

  // ── Edit mode ───────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ id: string; span: GridSpan }[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const narrow = useNarrow(wrapRef);
  const dragId = useRef<string | null>(null);

  const enterEdit = () => {
    setDraft(tiles.map((t) => ({ id: t.id, span: t.span })));
    setSaveError(null);
    setEditing(true);
  };
  const reorder = (targetId: string) => {
    const src = dragId.current;
    // A tile only moves within its own zone — ignore a drop across the boundary.
    if (!src || src === targetId || zoneOfId(src) !== zoneOfId(targetId)) return;
    setDraft((d) => {
      const from_ = d.findIndex((x) => x.id === src);
      const to_ = d.findIndex((x) => x.id === targetId);
      if (from_ < 0 || to_ < 0) return d;
      const next = [...d];
      const [moved] = next.splice(from_, 1);
      next.splice(to_, 0, moved);
      return next;
    });
  };
  const resize = (id: string, span: GridSpan) => setDraft((d) => d.map((x) => (x.id === id ? { ...x, span } : x)));

  const saveLayout = async () => {
    const byId = new Map(template.reports.map((r) => [r.id, r]));
    const reports: DraftReport[] = draft.flatMap(({ id, span }) => {
      const r = byId.get(id);
      if (!r) return [];
      // Metrics are never resized — don't persist a width for them.
      const keepSpan = zoneOfId(id) === 'content';
      return [{ id: r.id, title: r.title, question: r.question, widget: r.widget, ...(keepSpan ? { span } : {}) }];
    });
    setSaving(true);
    setSaveError(null);
    try {
      await updateTemplate(template.id, template.name, reports);
      setLocalLayout(draft);
      setEditing(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Couldn't save the layout.");
    } finally {
      setSaving(false);
    }
  };

  const exportDashboard = () => {
    const items: DashboardItem[] = tiles.flatMap((w) =>
      w.outcome.ok && w.outcome.result
        ? [{ title: w.report.title, result: w.outcome.result, insights: w.outcome.insights ?? null }]
        : [],
    );
    downloadDashboardReport(template.name, items);
  };

  const metricDraft = draft.filter((d) => zoneOfId(d.id) === 'metric');
  const contentDraft = draft.filter((d) => zoneOfId(d.id) === 'content');

  const renderZone = (zoneDraft: { id: string; span: GridSpan }[], resizable: boolean) => (
    <div style={GRID}>
      {zoneDraft.map(({ id, span }) => {
        const t = tileById.get(id);
        if (!t) return null;
        return (
          <EditTile
            key={id}
            id={id}
            span={span}
            narrow={narrow}
            resizable={resizable}
            onResize={resize}
            onDragStartId={(i) => (dragId.current = i)}
            onDropOnId={reorder}
          >
            <TileBody loaded={t} />
          </EditTile>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" onClick={onBack} style={backBtn} title="Back to templates" disabled={editing}>
            ← Templates
          </button>
          <strong style={{ fontSize: 18 }}>{template.name}</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {!editing ? (
            <>
              <label style={dateLabel}>
                From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={dateInput} />
              </label>
              <label style={dateLabel}>
                To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={dateInput} />
              </label>
              {halfPicked ? (
                <span className="muted" style={{ fontSize: 12 }} role="status">
                  Pick {from ? 'a To' : 'a From'} date to apply the range
                </span>
              ) : null}
              <button type="button" onClick={exportDashboard} disabled={!loaded} style={exportBtn} title="Export the whole dashboard">
                ⭳ Export
              </button>
              <button type="button" onClick={enterEdit} disabled={!loaded || tiles.length === 0} style={exportBtn} title="Rearrange and resize tiles">
                ✎ Edit layout
              </button>
            </>
          ) : (
            <>
              <span className="muted" style={{ fontSize: 12 }}>Metrics: drag to reorder · charts: drag &amp; resize</span>
              <button type="button" onClick={() => setEditing(false)} disabled={saving} style={backBtn}>Cancel</button>
              <button type="button" onClick={saveLayout} disabled={saving} style={saveBtn}>{saving ? 'Saving…' : 'Save layout'}</button>
            </>
          )}
        </div>
      </div>

      {saveError ? <p style={{ color: 'var(--bad, #c0392b)', fontSize: 13, margin: 0 }}>{saveError}</p> : null}

      {loaded === null ? (
        <PageLoader
          label={`Running ${done} of ${template.reports.length} report${template.reports.length === 1 ? '' : 's'}…`}
          percent={template.reports.length ? (done / template.reports.length) * 100 : 0}
        />
      ) : tiles.length === 0 ? (
        <p className="muted">This template has no reports yet — edit it to add some.</p>
      ) : (
        <div ref={wrapRef} style={{ display: 'grid', gap: 12 }}>
          {editing ? (
            <>
              {metricDraft.length > 0 ? <ZoneHeading>Metrics</ZoneHeading> : null}
              {metricDraft.length > 0 ? renderZone(metricDraft, false) : null}
              {contentDraft.length > 0 ? <ZoneHeading>Charts &amp; graphs</ZoneHeading> : null}
              {contentDraft.length > 0 ? renderZone(contentDraft, true) : null}
            </>
          ) : (
            <>
              {metricTiles.length > 0 ? (
                <div style={GRID}>
                  {metricTiles.map((t) => (
                    <div key={t.id} style={{ gridColumn: `span ${narrow ? 12 : t.span}` }}><TileBody loaded={t} /></div>
                  ))}
                </div>
              ) : null}
              {contentTiles.length > 0 ? (
                <div style={GRID}>
                  {contentTiles.map((t) => (
                    <div key={t.id} style={{ gridColumn: `span ${narrow ? 12 : t.span}` }}><TileBody loaded={t} /></div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const GRID: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 16 };

function ZoneHeading({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</div>;
}

/** The tile content: a KPI number, a chart/table card, or a failure note. */
function TileBody({ loaded }: { loaded: Loaded }): React.JSX.Element {
  if (loaded.kind === 'kpi') {
    return (
      <div className="card" style={{ padding: 14, height: '100%' }}>
        <KpiWidget loaded={loaded} />
      </div>
    );
  }
  if (!loaded.kind) {
    return (
      <div className="card" style={{ padding: 14, height: '100%' }}>
        <div style={cardTitle}>{loaded.report.title}</div>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
          Couldn’t load: {loaded.outcome.ok ? 'no data' : loaded.outcome.reason}
        </p>
      </div>
    );
  }
  return <WidgetCard loaded={loaded} />;
}

/**
 * One tile in edit mode: draggable (native HTML5 drag, bar handle only) to reorder
 * within its zone. Content tiles also carry a corner grip that snaps the width to
 * ¼/⅓/½/full; metric tiles are drag-only (`resizable` false). The chart inside is
 * inert while editing so a drag never lands as a legend click.
 */
function EditTile({
  id, span, narrow, resizable, onResize, onDragStartId, onDropOnId, children,
}: {
  id: string;
  span: GridSpan;
  narrow: boolean;
  resizable: boolean;
  onResize: (id: string, span: GridSpan) => void;
  onDragStartId: (id: string) => void;
  onDropOnId: (id: string) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const cellRef = useRef<HTMLDivElement | null>(null);

  const onGripDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const left = cellRef.current?.getBoundingClientRect().left ?? 0;
    // The 12-col grid is the tile's parent — measure it for the column width.
    const gridWidth = cellRef.current?.parentElement?.getBoundingClientRect().width ?? 1;
    const move = (ev: PointerEvent) => onResize(id, snapSpan(((ev.clientX - left) / gridWidth) * 12));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      ref={cellRef}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onDropOnId(id); }}
      style={{ gridColumn: `span ${narrow ? 12 : span}`, position: 'relative', border: '1px dashed var(--series-1)', borderRadius: 10, background: 'var(--surface-1)', padding: 4 }}
    >
      {/* Only the bar is the drag handle — so dragging the resize grip never starts
          a tile move, and the drop target stays the whole cell. */}
      <div
        draggable
        onDragStart={(e) => { onDragStartId(id); e.dataTransfer.effectAllowed = 'move'; }}
        style={dragBar}
      >
        <span aria-hidden="true">⣿</span> Drag to move
        {resizable ? <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{SPAN_LABEL[span]}</span> : null}
      </div>
      <div style={{ pointerEvents: 'none' }}>{children}</div>
      {resizable && !narrow ? (
        <div onPointerDown={onGripDown} title="Drag to resize" style={resizeGrip}>⇲</div>
      ) : null}
    </div>
  );
}

/** True while the grid is too narrow for a multi-column layout → every tile goes full-width. */
function useNarrow(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width < 700));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return narrow;
}

function KpiWidget({ loaded }: { loaded: Loaded }): React.JSX.Element {
  const result = loaded.outcome.ok ? loaded.outcome.result : null;
  const metric = result?.columns.find((c) => c.kind === 'metric');
  const value: ResultValue = metric && result ? (result.rows[0]?.[metric.key] ?? '—') : '—';
  return <StatTile label={loaded.report.title} value={value} />;
}

function WidgetCard({ loaded }: { loaded: Loaded }): React.JSX.Element {
  const result = loaded.outcome.ok ? loaded.outcome.result : null;
  const viz = loaded.viz ?? 'table';
  const vizResult: QueryResult | null = result ? { ...result, plan: { ...result.plan, visualization: viz } } : null;
  return (
    <div className="card" style={{ padding: 14, height: '100%' }}>
      <div style={cardTitle}>{loaded.report.title}</div>
      {vizResult ? <Chart result={vizResult} /> : <p className="muted" style={{ fontSize: 13 }}>No data.</p>}
    </div>
  );
}

/**
 * Turn a From/To range into a natural-language suffix the planner applies as a
 * date filter. The one-sided cases stay supported for any other caller, but the
 * dashboard never asks for one — it commits only a complete or empty range.
 */
function rangeSuffix(from: string, to: string): string {
  if (from && to) return ` from ${from} to ${to}`;
  if (from) return ` since ${from}`;
  if (to) return ` up to ${to}`;
  return '';
}

const headerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 14, flexWrap: 'wrap' };
const dateLabel: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 };
const dateInput: React.CSSProperties = { padding: '4px 6px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--panel, transparent)', color: 'var(--text)' };
const exportBtn: React.CSSProperties = { padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' };
const backBtn: React.CSSProperties = { padding: '5px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' };
const saveBtn: React.CSSProperties = { padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: '1px solid var(--series-1)', background: 'var(--series-1)', color: '#fff', cursor: 'pointer' };
const cardTitle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 };
const dragBar: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, cursor: 'grab', userSelect: 'none', fontSize: 12, color: 'var(--text-muted)', padding: '2px 4px 6px' };
const resizeGrip: React.CSSProperties = { position: 'absolute', right: 4, bottom: 4, width: 18, height: 18, cursor: 'ew-resize', color: 'var(--series-1)', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', lineHeight: 1, fontSize: 14 };
